import * as PIXI from 'pixi.js';
import {
  computeResultFromStartAndLines,
  deriveByRandomness,
  normalizeRandomnessHex,
  type MinionsResult,
  type MinionsStart,
} from '../shared/minionsLogic';
import {
  fetchHistory,
  fetchLadderStatusWithLatest,
  getMinionsApiBaseURL,
  isStatusOkFalse,
  ladderStatusFromJsonRaw,
  parseEmbeddedLatestFromStatusRaw,
  parseIntervalSec,
  type LadderResultPayload,
  type LadderStatus,
} from '../api/minionsClient';
import {
  createMinionsApp,
  LadderBoard,
  minionsRendererResolution,
  readCanvasContainerSize,
} from './LadderBoard';
import { MinionsCanvasUI } from './MinionsCanvasUI';
import type { StatusHudModel } from '../ui/minionsHud';

declare global {
  interface Window {
    /** file 빌드/임베드에서 런타임에 주입하는 WS URL (app.js 보다 먼저 설정) */
    __MINIONS_WS_URL__?: string;
    /** 레거시 번들 호환: window.MINIONS_LADDER_WS_URL 를 먼저 보던 빌드가 존재 */
    MINIONS_LADDER_WS_URL?: string;
  }
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

function parseEnvIntMs(raw: unknown, fallback: number, min: number): number {
  if (raw == null || raw === '') return fallback;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < min) return fallback;
  return Math.floor(n);
}

/** action=status 주기 폴링(앵커). 뷰는 bettingSyncRemain + 로컬 시계로 초 표시 */
function statusPollIntervalMs(): number {
  return parseEnvIntMs(import.meta.env.VITE_MINIONS_STATUS_POLL_MS, 12_000, 3000);
}

/**
 * WS 없을 때: status 한 요청에 회차가 없으면 embeddedResult 가 비므로,
 * 같은 URL 을 조금 더 자주 호출해 결과를 status 응답에 실어 주도록 유도(PHP include_latest).
 */
function resultPollIntervalNoWsMs(): number {
  return parseEnvIntMs(import.meta.env.VITE_MINIONS_RESULT_POLL_MS, 8000, 2000);
}

function combinedPollIntervalMs(wsConnected: boolean): number {
  const statusI = statusPollIntervalMs();
  if (wsConnected) return statusI;
  return Math.min(statusI, resultPollIntervalNoWsMs());
}

/** WS 로 status·history 를 받으면 HTTP 폴링 생략 (재연결·강제 시에만 HTTP) */
function envForceHttpSync(): boolean {
  return (
    import.meta.env.VITE_MINIONS_FORCE_HTTP_SYNC === '1' ||
    import.meta.env.VITE_MINIONS_FORCE_HTTP_SYNC === 'true'
  );
}

/** 서버 betting_remaining_seconds 를 최대 이 초까지만 앵커(홀수 블록 ~24초 전제 시 20초 후 로컬 마감). 0 = 비활성 */
function bettingCountdownCapSec(): number {
  return parseEnvIntMs(import.meta.env.VITE_MINIONS_BETTING_COUNTDOWN_CAP, 20, 0);
}

function normalizePayload(raw: LadderResultPayload): {
  block: number | null;
  hash: string;
  start: MinionsStart;
  lines: 3 | 4;
  result: MinionsResult;
} | null {
  const h =
    raw.hash ||
    raw.randomnessHex ||
    raw.randomness ||
    '';
  const derived = h ? deriveByRandomness(String(h)) : null;
  let start: MinionsStart;
  let lines: 3 | 4;
  let result: MinionsResult;
  if (derived) {
    start = derived.start;
    lines = derived.lines;
    result = derived.result;
  } else {
    if (raw.start !== 'left' && raw.start !== 'right') return null;
    start = raw.start;
    lines = Number(raw.lines) === 3 ? 3 : 4;
    result =
      raw.result === 'odd' || raw.result === 'even'
        ? raw.result
        : computeResultFromStartAndLines(start, lines);
  }
  const b = raw.current_block ?? raw.block;
  const block = b != null && String(b).trim() !== '' ? Number(b) : null;
  return {
    block: Number.isFinite(block as number) ? (block as number) : null,
    hash: normalizeRandomnessHex(String(h)) ? '0x' + normalizeRandomnessHex(String(h)) : String(h),
    start,
    lines,
    result,
  };
}

type NormalizedRound = NonNullable<ReturnType<typeof normalizePayload>>;

export class MinionsApp {
  private app: PIXI.Application;
  private board: LadderBoard;
  private canvasUI: MinionsCanvasUI;
  private lastHashNorm = '';
  private running = true;
  private statusModel: StatusHudModel = {
    bettingSeconds: null,
    nextBlock: null,
    bettingClosed: false,
    hint: 'minionsAPI · status/WS 확인',
  };
  private historyCache: Record<string, unknown>[] = [];
  /** 사다리 연출 중 전광판 숨김 (ethminions.php consumeRound) */
  private scoreboardVisible = true;
  /** 서버 status 앵커(남은 초) + 수신 시각 — 전광판은 1초 타이머로 로컬 감소 */
  private bettingSyncRemain: number | null = null;
  private bettingSyncAt = 0;
  private hudSecondTimer: ReturnType<typeof setInterval> | null = null;
  /** resize/visualViewport 폭주 시 한 프레임으로 묶기 */
  private resizeRaf = 0;
  /** 상위 레이아웃(사이트 헤더·flex 영역)만 바뀌어도 맞춤 */
  private appResizeObserver: ResizeObserver | null = null;
  /** DevTools 모바일 모드 등에서 #app 보다 inner* 가 먼저 줄 때 */
  private bodyResizeObserver: ResizeObserver | null = null;
  private boundWheel: (e: WheelEvent) => void;
  /**
   * ladder API 가 배팅 중에도 ok 를 주면 consume 가 먼저 돌아 lastHash 가 박혀,
   * 실제 마감 시점에는 중복 처리로 내역·연출이 스킵된다. 마감까지 보류한다.
   */
  private pendingRound: NormalizedRound | null = null;
  private pendingRoundSinceMs = 0;
  /** WS로 블록 결과를 받는 채널이 붙어 있으면 HTTP 결과 폴링은 느리게만 */
  private wsResultChannelConnected = false;
  private statusSyncInFlight = false;
  /** action=history 는 status 보다 드물게 — 서버 내역과 캔버스 동기화 */
  private lastHistoryPullAt = 0;
  private historyResyncTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly HISTORY_PULL_MIN_MS = 25_000;
  /** WS hello 등으로 캐시가 커지면 매 refresh 파싱 비용·메모리 증가 — 최신 N건 만 유지 */
  private static readonly HISTORY_CACHE_MAX = 500;
  /** WS minions_status — 사다리 연출 중엔 보류, 전광판(다음 회차 타이머)은 연출 종료 후 적용 */
  private pendingStatusAfterReveal: Record<string, unknown> | null = null;
  private revealScoreboardTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly boundVisibilitySync = () => {
    if (!this.running || typeof document === 'undefined') return;
    if (document.visibilityState === 'visible') {
      this.lastHistoryPullAt = 0;
      void this.syncStatusOnce();
    }
  };
  private readonly boundOnlineSync = () => {
    if (!this.running) return;
    this.lastHistoryPullAt = 0;
    void this.syncStatusOnce();
  };
  private readonly boundLoadResize = () => {
    if (!this.running) return;
    this.scheduleResize();
  };

  constructor(canvas: HTMLCanvasElement) {
    this.boundWheel = (e: WheelEvent) => {
      const v = this.app.view as HTMLCanvasElement;
      const b = v.getBoundingClientRect();
      const sx = this.app.renderer.width / b.width;
      const sy = this.app.renderer.height / b.height;
      this.canvasUI.handleWheel(
        (e.clientX - b.left) * sx,
        (e.clientY - b.top) * sy,
        e.deltaY
      );
    };

    this.app = createMinionsApp(canvas);
    this.canvasUI = new MinionsCanvasUI(
      () => this.renderHud(),
      this.app.view as HTMLCanvasElement
    );
    this.board = new LadderBoard();
    this.canvasUI.mountBoard(this.board);
    this.app.stage.addChild(this.canvasUI);

    this.app.view.addEventListener('wheel', this.boundWheel, { passive: true });

    this.performResize();
    const host = (this.app.view as HTMLCanvasElement).parentElement;
    if (host && typeof ResizeObserver !== 'undefined') {
      this.appResizeObserver = new ResizeObserver(() => this.scheduleResize());
      this.appResizeObserver.observe(host);
    }
    if (typeof ResizeObserver !== 'undefined' && typeof document !== 'undefined' && document.body) {
      this.bodyResizeObserver = new ResizeObserver(() => this.scheduleResize());
      this.bodyResizeObserver.observe(document.body);
    }
    window.addEventListener('resize', () => this.scheduleResize());
    window.addEventListener('orientationchange', () => this.scheduleResize());
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', () => this.scheduleResize());
      vv.addEventListener('scroll', () => this.scheduleResize());
    }
    if (typeof document !== 'undefined') {
      if (document.readyState === 'complete') {
        queueMicrotask(() => this.scheduleResize());
      } else {
        window.addEventListener('load', this.boundLoadResize);
      }
    }
    void this.canvasUI.loadEthAssets().then(() => this.renderHud());
    this.renderHud();
    document.addEventListener('visibilitychange', this.boundVisibilitySync);
    window.addEventListener('online', this.boundOnlineSync);
    void this.initWs();
    void this.loadHistoryOnce();
    this.pollStatus();

    this.hudSecondTimer = setInterval(() => {
      if (this.running) this.tickHudLight();
    }, 1000);
  }

  /** 폴링 사이에도 초가 줄어 보이도록 (마지막 서버 초 + 로컬 경과 — betting_allowed=false 도 초가 있으면 감소) */
  private liveDisplayBettingSeconds(): number | null {
    if (this.bettingSyncRemain != null) {
      const elapsed = Math.floor((Date.now() - this.bettingSyncAt) / 1000);
      return Math.max(0, this.bettingSyncRemain - elapsed);
    }
    return this.statusModel.bettingSeconds;
  }

  /** 전광판과 동일 기준: 서버가 준 남은 시간을 ms 단위로(소수 초 반영) */
  private liveBettingRemainMs(): number {
    if (this.bettingSyncRemain != null) {
      const elapsed = Date.now() - this.bettingSyncAt;
      return Math.max(0, this.bettingSyncRemain * 1000 - elapsed);
    }
    return 0;
  }

  /**
   * 결과 수신 시점에 이미 다음 라운드 배팅(예: 남은 19초)으로 status 가 바뀐 경우가 있어,
   * 막판·마감 구간에서만 "남은 시간"과 사다리 경로 진행을 같은 시계로 맞춤.
   */
  private shouldSyncLadderAnimWithCountdown(): boolean {
    const live = this.liveDisplayBettingSeconds();
    if (live != null && Number.isFinite(live) && live > 6) return false;
    if (this.statusModel.bettingClosed) return true;
    if (this.statusModel.serverBettingAllowed === false) return true;
    if (live != null && Number.isFinite(live) && live <= 6) return true;
    return false;
  }

  private ladderAnimTiming(): {
    durationMs: number;
    easing: 'linear' | 'easeOutCubic';
  } {
    if (!this.shouldSyncLadderAnimWithCountdown()) {
      return { durationMs: 880, easing: 'easeOutCubic' };
    }

    const liveSec = this.liveDisplayBettingSeconds();
    /*
     * bettingSyncRemain 없음 → remainMs만 보면 0으로 떨어져 880ms 급진행되는 경우가 있음.
     * 전광판 초(1~6)를 폴백으로 쓴 뒤, 표시 초와 서버 ms가 어긋나도 [대략 한 칸] 안으로 맞춤.
     */
    let rawMs =
      this.bettingSyncRemain != null
        ? this.liveBettingRemainMs()
        : liveSec != null && liveSec >= 1 && liveSec <= 6
          ? liveSec * 1000
          : this.liveBettingRemainMs();

    if (
      liveSec != null &&
      Number.isFinite(liveSec) &&
      liveSec >= 1 &&
      liveSec <= 6
    ) {
      const lo = Math.max(600, liveSec * 1000 - 800);
      const hi = liveSec * 1000 + 1500;
      rawMs = Math.min(Math.max(rawMs, lo), hi);
    }

    if (rawMs < 100 || rawMs > 15000) {
      return { durationMs: 880, easing: 'easeOutCubic' };
    }
    return {
      durationMs: Math.max(600, Math.min(rawMs, 15000)),
      easing: 'linear',
    };
  }

  /** 캡 적용 후 로컬 초가 0이면 서버가 더 길게 줘도 마감 구간으로 간주 */
  private localBettingCapExhausted(): boolean {
    const cap = bettingCountdownCapSec();
    if (cap <= 0 || this.bettingSyncRemain == null) return false;
    const live = this.liveDisplayBettingSeconds();
    return live != null && Number.isFinite(live) && live <= 0;
  }

  /** 배팅이 열려 있고 남은 시간이 있으면 결과 연출·내역 반영을 하지 않음 */
  private canRevealRoundNow(): boolean {
    if (this.localBettingCapExhausted()) return true;
    if (this.statusModel.bettingClosed) return true;
    if (this.statusModel.serverBettingAllowed === false) return true;
    const live = this.liveDisplayBettingSeconds();
    if (live != null && Number.isFinite(live) && live <= 0) return true;
    if (this.liveBettingRemainMs() <= 200) return true;
    /* allowed === false 는 위에서 이미 마감으로 처리됨 */
    const bettingOpen = live != null && Number.isFinite(live) && live >= 1;
    if (bettingOpen) return false;
    return true;
  }

  private shouldForceFlushPendingRound(): boolean {
    if (!this.pendingRound) return false;
    return Date.now() - this.pendingRoundSinceMs > 90000;
  }

  private tryFlushPendingRound() {
    if (!this.pendingRound) return;
    if (!this.canRevealRoundNow() && !this.shouldForceFlushPendingRound()) return;
    const n = this.pendingRound;
    this.pendingRound = null;
    this.pendingRoundSinceMs = 0;
    const hNorm = normalizeRandomnessHex(n.hash);
    if (hNorm && hNorm === this.lastHashNorm) return;
    this.applyRoundReveal(n);
  }

  private performResize() {
    const { w, h } = this.measureViewSize();
    const r = minionsRendererResolution();
    if (this.app.renderer.resolution !== r) {
      this.app.renderer.resolution = r;
    }
    /* autoDensity 가 style 에 넣는 px 가 buffer 와 같은 종횡비여야 함. 100% 강제는 #app 과 불일치 시 세로 늘림 등 발생 */
    this.app.renderer.resize(w, h);
    this.renderHud();
  }

  private scheduleResize() {
    if (this.resizeRaf !== 0) cancelAnimationFrame(this.resizeRaf);
    this.resizeRaf = requestAnimationFrame(() => {
      this.resizeRaf = 0;
      if (!this.running) return;
      this.performResize();
      /* 모바일 에뮬·DVH 갱신 직후 한 프레임 늦게 #app 레이아웃이 맞는 경우 */
      requestAnimationFrame(() => {
        if (this.running) this.performResize();
      });
    });
  }

  /** 초기화·리사이즈 공통 — `readCanvasContainerSize` 가 가시 뷰포트 대비 과대 높이를 잘라 줌 */
  private measureViewSize(): { w: number; h: number } {
    return readCanvasContainerSize(this.app.view as HTMLCanvasElement);
  }

  renderHud() {
    const w = this.app.renderer.width;
    const h = this.app.renderer.height;
    const liveSec = this.liveDisplayBettingSeconds();
    const capMarucal = this.localBettingCapExhausted();
    const { boardW, boardH } = this.canvasUI.refresh(
      w,
      h,
      {
        ...this.statusModel,
        bettingSeconds: liveSec ?? this.statusModel.bettingSeconds,
        bettingClosed: this.statusModel.bettingClosed || capMarucal,
        bettingHoldAtZero: this.statusModel.bettingHoldAtZero || capMarucal,
        scoreboardVisible: this.scoreboardVisible,
      },
      this.historyCache
    );
    this.board.layoutInRect(boardW, boardH);
    this.tryFlushPendingRound();
  }

  /** 1초 타이머: 전광판·검증만 — 전체 레이아웃·내역 재빌드는 생략 */
  private tickHudLight() {
    const w = this.app.renderer.width;
    const h = this.app.renderer.height;
    const liveSec = this.liveDisplayBettingSeconds();
    const capMarucal = this.localBettingCapExhausted();
    this.canvasUI.tickHudOnly(w, h, {
      ...this.statusModel,
      bettingSeconds: liveSec ?? this.statusModel.bettingSeconds,
      bettingClosed: this.statusModel.bettingClosed || capMarucal,
      bettingHoldAtZero: this.statusModel.bettingHoldAtZero || capMarucal,
      scoreboardVisible: this.scoreboardVisible,
    });
    this.tryFlushPendingRound();
  }

  /** 마지막 status 응답을 모델에 반영 (폴링·포커스·WS 재연결 공통) */
  private applyLadderStatus(st: LadderStatus | null) {
    if (!st || typeof st !== 'object') {
      this.statusModel = {
        ...this.statusModel,
        hint:
          this.statusModel.nextBlock != null && this.statusModel.bettingSeconds != null
            ? this.statusModel.hint
            : '상태 응답 없음 — 프록시/서버 확인',
      };
      return;
    }
    const intervalNum = parseIntervalSec(st.interval);
    const fatal =
      isStatusOkFalse(st.ok) &&
      st.betting_remaining_seconds == null &&
      st.next_block == null &&
      intervalNum == null;

    if (fatal) {
      this.statusModel = {
        ...this.statusModel,
        bettingSeconds: null,
        bettingClosed: false,
        serverBettingAllowed: undefined,
        bettingHoldAtZero: false,
        hint: 'MINIONS_LADDER_API · minionsAPI(3020) 확인',
      };
      this.bettingSyncRemain = null;
    } else if (isStatusOkFalse(st.ok)) {
      /*
       * PHP action=status 분기: latest=null 이면 ok:false 인데 betting_allowed 없이
       * betting_remaining_seconds 만 오는 경우가 있음. 이걸 매 폴링마다 반영하면
       * ok:true 일 때와 화면이 번갈아 깜빡인다. 배팅·초는 마지막 정상(ok) 응답만 신뢰.
       */
      const nb =
        st.next_block !== undefined && st.next_block !== null && String(st.next_block).trim() !== ''
          ? st.next_block
          : this.statusModel.nextBlock;
      this.statusModel = {
        ...this.statusModel,
        nextBlock: nb,
      };
    } else {
      const allowedExplicit = st.betting_allowed;
      const allowed = allowedExplicit !== false;
      const sec =
        typeof st.betting_remaining_seconds === 'number' &&
        Number.isFinite(st.betting_remaining_seconds)
          ? Math.max(0, Math.floor(st.betting_remaining_seconds))
          : null;
      const cap = bettingCountdownCapSec();
      const secAnchored =
        sec !== null && cap > 0 ? Math.min(sec, cap) : sec;
      const brsRaw = st.betting_remaining_seconds;
      const holdAtZero =
        allowedExplicit === false &&
        (brsRaw == null ||
          !Number.isFinite(Number(brsRaw)) ||
          Math.floor(Number(brsRaw)) <= 0);

      if (secAnchored !== null) {
        this.bettingSyncRemain = secAnchored;
        this.bettingSyncAt = Date.now();
      }

      const nb =
        st.next_block !== undefined && st.next_block !== null && String(st.next_block).trim() !== ''
          ? st.next_block
          : this.statusModel.nextBlock;

      const bettingClosed =
        secAnchored !== null ? !allowed || secAnchored <= 0 : allowedExplicit === false;

      this.statusModel = {
        bettingSeconds:
          secAnchored !== null ? secAnchored : this.statusModel.bettingSeconds,
        nextBlock: nb,
        bettingClosed,
        serverBettingAllowed:
          typeof allowedExplicit === 'boolean' ? allowedExplicit : undefined,
        bettingHoldAtZero: holdAtZero,
        hint: undefined,
      };
    }
  }

  private useHttpForLadderSync(): boolean {
    if (envForceHttpSync()) return true;
    if (!import.meta.env.VITE_MINIONS_WS_URL?.trim()) return true;
    return !this.wsResultChannelConnected;
  }

  /**
   * action=status 형 JSON — 전광판·next_block·배팅 초만 반영.
   * @param consumeEmbeddedRound HTTP 폴링·레거시 전용. WS 푸시 모드에서는 false (회차는 minions_new_block 만).
   */
  private applyStatusPayloadFromServer(
    raw: Record<string, unknown>,
    consumeEmbeddedRound = false
  ) {
    const st = ladderStatusFromJsonRaw(raw);
    this.applyLadderStatus(st);
    if (consumeEmbeddedRound) {
      const embedded = parseEmbeddedLatestFromStatusRaw(raw);
      if (embedded) this.consumeRound(embedded);
    }
  }

  /** 연출 중이면 전광판(다음 회차 타이머)만 연출 종료 후 적용. embedded 회차는 쓰지 않음. */
  private enqueueOrApplyStatusPayload(raw: Record<string, unknown>) {
    if (!this.scoreboardVisible) {
      this.pendingStatusAfterReveal = raw;
      return;
    }
    this.pendingStatusAfterReveal = null;
    this.applyStatusPayloadFromServer(raw, false);
  }

  /** hello history 반영 후, 연출 없이 최신 회차 정적 표시 */
  private syncStaticBoardFromHistoryCache() {
    if (this.historyCache.length === 0) return;
    const latest = this.historyCache[0];
    const hash = String(
      latest.randomness_hex || latest.hash || latest.randomness || ''
    );
    const d = hash ? deriveByRandomness(hash) : null;
    if (d) {
      this.board.drawStatic(d.start, d.lines, d.result, hash);
    }
    if (hash) {
      this.canvasUI.setVerifyRandomnessFromHash(hash);
    }
  }

  private async syncStatusOnce(): Promise<void> {
    if (!this.running || this.statusSyncInFlight) return;
    this.statusSyncInFlight = true;
    try {
      if (this.useHttpForLadderSync()) {
        try {
          const { status: st, embeddedResult } = await fetchLadderStatusWithLatest();
          this.applyLadderStatus(st);
          if (embeddedResult) {
            this.consumeRound(embeddedResult);
          }
        } catch {
          this.statusModel = {
            ...this.statusModel,
            bettingClosed: false,
            serverBettingAllowed: undefined,
            bettingHoldAtZero: false,
            hint: '상태 동기화 실패 — 프록시/서버 확인',
          };
        }
        await this.pullHistoryIfDue();
      }
      this.renderHud();
    } finally {
      this.statusSyncInFlight = false;
    }
  }

  private async pullHistoryIfDue(): Promise<void> {
    const now = Date.now();
    if (
      this.lastHistoryPullAt > 0 &&
      now - this.lastHistoryPullAt < MinionsApp.HISTORY_PULL_MIN_MS
    ) {
      return;
    }
    this.lastHistoryPullAt = now;
    try {
      const hist = await fetchHistory(50);
      if (hist != null && hist.length > 0) {
        this.mergeHistoryRecords(hist as Record<string, unknown>[]);
      }
    } catch {
      /* ignore */
    }
  }

  /** 라운드 연출 직후 서버 DB 내역과 맞춤(블록 번호 없는 페이로드 대비) */
  private scheduleHistoryResyncFromServer() {
    if (!this.useHttpForLadderSync()) return;
    if (this.historyResyncTimer != null) {
      clearTimeout(this.historyResyncTimer);
      this.historyResyncTimer = null;
    }
    this.historyResyncTimer = setTimeout(() => {
      this.historyResyncTimer = null;
      this.lastHistoryPullAt = 0;
      void this.pullHistoryIfDue().then(() => {
        if (this.running) this.renderHud();
      });
    }, 1800);
  }

  /**
   * WS 로 브로드캐스트만 쓰는 경우 HTTP action=status 폴링을 하지 않음.
   * WS 끊김·강제 플래그일 때만 주기적 GET.
   */
  private async pollStatus() {
    while (this.running) {
      if (this.useHttpForLadderSync()) {
        await this.syncStatusOnce();
        const wsUrl = import.meta.env.VITE_MINIONS_WS_URL?.trim();
        const wsOn = !!(wsUrl && this.wsResultChannelConnected);
        const jitter = Math.floor(Math.random() * 2500);
        await sleep(combinedPollIntervalMs(wsOn) + jitter);
      } else {
        await sleep(5000);
      }
    }
  }

  private mergeHistoryRecords(rows: Record<string, unknown>[]) {
    const byBlock = new Map<number, Record<string, unknown>>();
    for (const r of [...this.historyCache, ...rows]) {
      const b = Number(r.block_number ?? r.block ?? r.round ?? 0);
      if (!Number.isFinite(b) || b <= 0) continue;
      byBlock.set(b, r);
    }
    const merged = [...byBlock.values()].sort(
      (a, b) =>
        Number(b.block_number ?? b.block ?? b.round ?? 0) -
        Number(a.block_number ?? a.block ?? a.round ?? 0)
    );
    this.historyCache = merged.slice(0, MinionsApp.HISTORY_CACHE_MAX);
  }

  private async loadHistoryOnce() {
    if (
      import.meta.env.VITE_MINIONS_WS_URL?.trim() &&
      !envForceHttpSync()
    ) {
      return;
    }
    this.lastHistoryPullAt = 0;
    try {
      await this.pullHistoryIfDue();
      if (this.historyCache.length > 0) {
        const latest = this.historyCache[0];
        const hash = String(
          latest.randomness_hex || latest.hash || latest.randomness || ''
        );
        const d = hash ? deriveByRandomness(hash) : null;
        if (d) {
          this.board.drawStatic(d.start, d.lines, d.result, hash);
        }
        if (hash) {
          this.canvasUI.setVerifyRandomnessFromHash(hash);
        }
      }
      this.renderHud();
    } catch {
      /* ignore */
    }
  }

  /**
   * @param opts.fromTrustedPush 워처 WS `minions_new_block` — 체인 확정 푸시이므로 배팅 타이머와 무관하게 즉시 연출·내역 반영
   */
  private consumeRound(
    raw: LadderResultPayload,
    opts?: { fromTrustedPush?: boolean }
  ) {
    const n = normalizePayload(raw);
    if (!n) return;
    const hNorm = normalizeRandomnessHex(n.hash);
    if (hNorm && hNorm === this.lastHashNorm) return;

    if (!opts?.fromTrustedPush && !this.canRevealRoundNow()) {
      const ph = this.pendingRound ? normalizeRandomnessHex(this.pendingRound.hash) : '';
      if (hNorm && ph && hNorm === ph) return;
      if (!this.pendingRound) this.pendingRoundSinceMs = Date.now();
      this.pendingRound = n;
      return;
    }

    this.pendingRound = null;
    this.pendingRoundSinceMs = 0;
    this.applyRoundReveal(n, { fastAnim: opts?.fromTrustedPush === true });
  }

  private applyRoundReveal(
    n: NormalizedRound,
    animOpts?: { fastAnim?: boolean }
  ) {
    const hNorm = normalizeRandomnessHex(n.hash);
    if (hNorm && hNorm === this.lastHashNorm) return;
    if (hNorm) this.lastHashNorm = hNorm;

    if (this.revealScoreboardTimer != null) {
      clearTimeout(this.revealScoreboardTimer);
      this.revealScoreboardTimer = null;
    }

    this.scoreboardVisible = false;
    this.renderHud();

    const { durationMs, easing } = animOpts?.fastAnim
      ? { durationMs: 880, easing: 'easeOutCubic' as const }
      : this.ladderAnimTiming();
    this.board.drawStatic(n.start, n.lines, n.result, n.hash);
    this.board.drawWinnerPath(n.start, n.lines, n.result, n.hash, durationMs, easing);
    this.board.flashBackground();

    this.revealScoreboardTimer = window.setTimeout(() => {
      this.revealScoreboardTimer = null;
      this.scoreboardVisible = true;
      if (this.pendingStatusAfterReveal) {
        this.applyStatusPayloadFromServer(this.pendingStatusAfterReveal, false);
        this.pendingStatusAfterReveal = null;
      }
      this.renderHud();
    }, durationMs + 320);

    if (n.block != null) {
      this.mergeHistoryRecords([
        {
          block_number: n.block,
          start: n.start,
          lines: n.lines,
          result: n.result,
          randomness_hex: n.hash,
        },
      ]);
      this.renderHud();
    }
    if (n.hash) {
      this.canvasUI.setVerifyRandomnessFromHash(n.hash);
    }
    this.scheduleHistoryResyncFromServer();
  }

  private async initWs() {
    const injected =
      typeof window !== 'undefined'
        ? String(window.__MINIONS_WS_URL__ ?? window.MINIONS_LADDER_WS_URL ?? '').trim()
        : '';
    const url = (injected || import.meta.env.VITE_MINIONS_WS_URL || '').trim();
    if (!url) return;

    const bypassHealth =
      import.meta.env.VITE_MINIONS_WS_BYPASS_HEALTH === '1' ||
      import.meta.env.VITE_MINIONS_WS_BYPASS_HEALTH === 'true';

    if (!bypassHealth) {
      const apiBase = getMinionsApiBaseURL();
      let healthUrl: string | null = null;
      if (typeof window !== 'undefined') {
        if (apiBase.startsWith('http')) {
          healthUrl = `${apiBase}/health`;
        } else if (apiBase.startsWith('/')) {
          healthUrl = `${window.location.origin}${apiBase}/health`;
        }
      }
      if (!healthUrl) return;

      try {
        const ac = new AbortController();
        const t = window.setTimeout(() => ac.abort(), 2500);
        const r = await fetch(healthUrl, { cache: 'no-store', signal: ac.signal });
        window.clearTimeout(t);
        if (!r.ok) return;
        const j = (await r.json()) as { watcher?: boolean };
        if (j?.watcher !== true) return;
      } catch {
        /* /health 실패 시 WS 미시도 — 콘솔 WebSocket failed 스팸 방지, HTTP 폴링으로 동작 */
        return;
      }
    }

    const connect = () => {
      let ws: WebSocket;
      try {
        ws = new WebSocket(url);
      } catch {
        return;
      }
      ws.onopen = () => {
        this.wsResultChannelConnected = true;
        this.renderHud();
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data as string);
          if (msg?.type === 'minions_hello') {
            if (Array.isArray(msg.history)) {
              const rows: Record<string, unknown>[] = [];
              for (const h of msg.history) {
                if (!h || typeof h !== 'object') continue;
                const o = h as Record<string, unknown>;
                const b = Number(o.block ?? o.block_number ?? 0);
                if (!Number.isFinite(b) || b <= 0) continue;
                rows.push({
                  block_number: b,
                  start: o.start,
                  lines: o.lines,
                  result: o.result,
                  randomness_hex: o.randomness_hex ?? o.randomness ?? o.hash ?? '',
                  created_at: o.created_at,
                });
              }
              if (rows.length) {
                this.mergeHistoryRecords(rows);
                this.syncStaticBoardFromHistoryCache();
              }
            }
            if (msg.status && typeof msg.status === 'object') {
              this.enqueueOrApplyStatusPayload(msg.status as Record<string, unknown>);
            }
            this.renderHud();
            return;
          }
          if (msg?.type === 'minions_status' && msg && typeof msg === 'object') {
            const raw = { ...(msg as Record<string, unknown>) };
            delete raw.type;
            this.enqueueOrApplyStatusPayload(raw);
            this.renderHud();
            return;
          }
          if (msg?.type === 'minions_new_block') {
            this.consumeRound(
              {
                ok: true,
                current_block: msg.round,
                block: msg.round,
                randomnessHex: msg.randomnessHex,
                start: msg.start,
                lines: msg.lines,
                result: msg.result,
              },
              { fromTrustedPush: true }
            );
          }
        } catch {
          /* ignore */
        }
      };
      ws.onclose = () => {
        this.wsResultChannelConnected = false;
        setTimeout(connect, 5000);
      };
    };
    connect();
  }

  destroy() {
    this.running = false;
    if (this.appResizeObserver != null) {
      this.appResizeObserver.disconnect();
      this.appResizeObserver = null;
    }
    if (this.bodyResizeObserver != null) {
      this.bodyResizeObserver.disconnect();
      this.bodyResizeObserver = null;
    }
    if (this.resizeRaf !== 0) {
      cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = 0;
    }
    if (this.revealScoreboardTimer != null) {
      clearTimeout(this.revealScoreboardTimer);
      this.revealScoreboardTimer = null;
    }
    this.pendingStatusAfterReveal = null;
    if (this.historyResyncTimer != null) {
      clearTimeout(this.historyResyncTimer);
      this.historyResyncTimer = null;
    }
    document.removeEventListener('visibilitychange', this.boundVisibilitySync);
    window.removeEventListener('online', this.boundOnlineSync);
    window.removeEventListener('load', this.boundLoadResize);
    if (this.hudSecondTimer != null) {
      clearInterval(this.hudSecondTimer);
      this.hudSecondTimer = null;
    }
    (this.app.view as HTMLCanvasElement).removeEventListener(
      'wheel',
      this.boundWheel
    );
    this.canvasUI.destroyVerifyOverlay();
    this.app.destroy(true, {
      children: true,
      texture: true,
      baseTexture: true,
    });
  }
}
